import { PropsWithChildren, useCallback } from "react";
import ReactSelect, { GroupBase, Props, StylesConfig } from "react-select";
import ReactAsyncSelect, { AsyncProps } from "react-select/async";
import { Control } from "design-system/xcore/Select/Control";
import { DropdownIndicator } from "design-system/xcore/Select/DropdownIndicator";
import { Group } from "design-system/xcore/Select/Group";
import { GroupHeading } from "design-system/xcore/Select/GroupHeading";
import { IndicatorSeparator } from "design-system/xcore/Select/IndicatorSeparator";
import { Input } from "design-system/xcore/Select/Input";
import { Menu } from "design-system/xcore/Select/Menu";
import { MultiValue, MultiValueContainer, MultiValueRemove } from "design-system/xcore/Select/MultiValue";
import { NoOptionsMessage } from "design-system/xcore/Select/NoOptionsMessage";
import { Option } from "design-system/xcore/Select/Option";
import { Placeholder } from "design-system/xcore/Select/Placeholder";
import { SingleValue } from "design-system/xcore/Select/SingleValue";
import { ValueContainer } from "design-system/xcore/Select/ValueContainer";
import { useLayout } from "xcore";
import { useIsClient } from "@robe/client";

export type OptionType<T extends unknown = unknown> = { label: string; value: T };

const createSelectStyles = <T extends OptionType = OptionType>(): StylesConfig<T, boolean> => ({
  menu: (styles) => ({ ...styles, zIndex: 2 }),
});

export interface SelectProps<T extends OptionType, M extends boolean> extends Props<T, M> {
  isSelectInModal?: boolean;
}

const Select = <T extends OptionType, M extends boolean>({ isSelectInModal, ...props }: SelectProps<T, M>) => {
  const { general, stringify } = useLayout();
  const isClient = useIsClient();
  if (!isClient) {
    return null;
  }

  const styles = createSelectStyles<T>();

  return (
    <ReactSelect<T, M>
      styles={styles}
      {...(!isSelectInModal && {
        classNames: {
          menuPortal: () => "robe-react-select-portal",
        },
      })}
      isClearable={false}
      components={{
        Option,
        Menu,
        IndicatorSeparator,
        DropdownIndicator,
        ValueContainer,
        Control,
        Placeholder,
        Input,
        SingleValue,
        MultiValue,
        MultiValueRemove,
        MultiValueContainer,
        NoOptionsMessage,
        GroupHeading,
        Group,
      }}
      placeholder={stringify(general.values.selectPlaceholder)}
      {...props}
    />
  );
};

export default Select;

interface AsyncSelectProps<T extends OptionType = OptionType>
  extends Omit<PropsWithChildren<AsyncProps<T, boolean, GroupBase<T>>>, "loadOptions" | "onChange"> {
  loadOptions: (inputValue: string) => Promise<T[]>;
  loadOptionsDebouncedDelay?: number;
  onChange: (value: T[]) => void;
  isSelectInModal?: boolean;
}

export const AsyncSelect = <T extends OptionType = OptionType>({
  loadOptions,
  loadOptionsDebouncedDelay,
  onChange,
  isSelectInModal,
  ...props
}: AsyncSelectProps<T>) => {
  const { general, stringify } = useLayout();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadOptionsDebounced = useCallback(
    debounce((inputValue: string, callback: (options: T[]) => void) => {
      loadOptions(inputValue).then((options) => callback(options));
    }, loadOptionsDebouncedDelay ?? 500),
    []
  );

  const client = useIsClient();
  if (!client) {
    return null;
  }

  const styles = createSelectStyles<T>();

  return (
    <ReactAsyncSelect<T, boolean>
      styles={styles}
      isClearable={false}
      {...(!isSelectInModal && {
        classNames: {
          menuPortal: () => "robe-react-select-portal",
        },
      })}
      components={{
        Option,
        Menu,
        IndicatorSeparator,
        DropdownIndicator,
        ValueContainer,
        Control,
        Placeholder,
        Input,
        SingleValue,
        MultiValue,
        MultiValueRemove,
        MultiValueContainer,
        NoOptionsMessage,
      }}
      placeholder={stringify(general.values.selectPlaceholder)}
      loadOptions={loadOptionsDebounced}
      onChange={onChange as any}
      {...props}
    />
  );
};

/**
 * `useDebouncedCallback` is not compatible with `react-select` async version: https://stackoverflow.com/a/78928545/13858940
 */
function debounce<T extends (...args: Parameters<T>) => ReturnType<T>>(fn: T, delay = 500): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      fn(...args);
    }, delay);
  };
}
